import {
  normalizeSearchExpression,
  normalizeSearchExpressionNegated,
  normalizeSearchExpressionOperator,
  searchExpressionNodeHasPatchBound,
} from "./core.js";
import {
  normalizeSearchQueryEnvelope,
  patchWindowFromExpressionNode,
} from "./envelope.js";
import {
  normalizeLayerFactCondition,
  normalizeLayerFactConditions,
  normalizeLayerFilterCompareOperator,
  normalizePatchBound,
  normalizePatchId,
  normalizeSearchTerm,
  normalizeSourceKinds,
  tradeNpcKeyFromSearchTerm,
} from "./terms.js";
import { parseSearchExpressionZoneRgb } from "./resolve.js";
import { isPlainObject } from "./shared.js";

const PRECEDENCE = Object.freeze({
  or: 1,
  and: 2,
});

const LAYER_FACT_CONDITION_OPERATOR_TEXT = Object.freeze({
  eq: "=",
  ne: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  "eq-ci": "~=",
  "contains-ci": "~",
});

const LAYER_FILTER_COMPARE_OPERATOR_TEXT = Object.freeze({
  eq: "=",
  "eq-ci": "~=",
  ne: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
});

function safeDslValue(value) {
  const normalized = String(value ?? "").trim();
  if (/^[A-Za-z0-9_.-]+$/.test(normalized)) {
    return normalized;
  }
  return JSON.stringify(normalized);
}

function formatZoneRgb(value) {
  const zoneRgb = parseSearchExpressionZoneRgb(value);
  return zoneRgb == null ? "" : `0x${zoneRgb.toString(16).padStart(6, "0")}`;
}

function formatSourceScope(sourceKinds) {
  const normalized = normalizeSourceKinds(sourceKinds);
  return normalized.length ? `@${normalized.map(safeDslValue).join(",")}` : "";
}

function formatLayerFactConditionValue(value) {
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return safeDslValue(value);
}

function formatLayerFactCondition(condition) {
  const normalized = normalizeLayerFactCondition(condition);
  if (!normalized) {
    return "";
  }
  if (normalized.op === "exists") {
    return safeDslValue(normalized.path);
  }
  const operator = LAYER_FACT_CONDITION_OPERATOR_TEXT[normalized.op] || "=";
  return `${safeDslValue(normalized.path)}${operator}${formatLayerFactConditionValue(normalized.value)}`;
}

function formatLayerFactConditions(conditions) {
  const normalized = normalizeLayerFactConditions(conditions);
  const text = normalized.map(formatLayerFactCondition).filter(Boolean);
  return text.length ? `[${text.join(",")}]` : "";
}

function formatLayerFilterSubject(subject) {
  const kind = String(subject?.kind ?? "").trim();
  if (kind === "fish") {
    return `fish:${Number.parseInt(subject.fishId, 10)}`;
  }
  if (kind === "item") {
    return `item:${Number.parseInt(subject.itemId, 10)}`;
  }
  if (kind === "item-group") {
    const itemIds = Array.isArray(subject.itemIds)
      ? subject.itemIds.map((itemId) => Number.parseInt(itemId, 10)).filter((itemId) => Number.isInteger(itemId))
      : [];
    return `items:[${itemIds.join(",")}]`;
  }
  if (kind === "zone") {
    return `zone:${formatZoneRgb(subject.zoneRgb)}`;
  }
  return safeDslValue(kind);
}

function formatLayerFilterPercent(value) {
  const basisPoints = Number.parseInt(value?.basisPoints ?? value?.basis_points, 10);
  if (!Number.isInteger(basisPoints) || basisPoints < 0) {
    return "";
  }
  const whole = Math.trunc(basisPoints / 100);
  const fraction = basisPoints % 100;
  if (fraction === 0) {
    return `${whole}%`;
  }
  return `${whole}.${String(fraction).padStart(2, "0").replace(/0+$/, "")}%`;
}

function formatLayerFilterRateComparison(comparison) {
  const operator = LAYER_FILTER_COMPARE_OPERATOR_TEXT[normalizeLayerFilterCompareOperator(comparison?.op)] || "";
  const value = formatLayerFilterPercent(comparison?.value);
  return operator && value ? `${operator} ${value}` : "";
}

function formatLayerFilterTypedValue(value) {
  const kind = String(value?.kind ?? "").trim().toLowerCase();
  if (kind === "percent") {
    return formatLayerFilterPercent(value);
  }
  if (kind === "number" || kind === "boolean") {
    return String(value.value);
  }
  return safeDslValue(value?.value);
}

function formatLayerFilterPredicate(predicate, parentPrecedence = 0) {
  if (!isPlainObject(predicate)) {
    return "";
  }
  if (predicate.kind === "bool") {
    const op = normalizeSearchExpressionOperator(predicate.op);
    const precedence = PRECEDENCE[op];
    const children = (Array.isArray(predicate.children) ? predicate.children : [])
      .map((child) => formatLayerFilterPredicate(child, precedence))
      .filter(Boolean);
    if (!children.length) {
      return "";
    }
    let text = children.join(` ${op.toUpperCase()} `);
    if (predicate.negated === true) {
      text = `NOT (${text})`;
    } else if (precedence < parentPrecedence && children.length > 1) {
      text = `(${text})`;
    }
    return text;
  }
  if (predicate.kind === "subject-presence") {
    return formatLayerFilterSubject(predicate.subject);
  }
  if (predicate.kind === "membership") {
    const subject = formatLayerFilterSubject(predicate.subject);
    const group = safeDslValue(predicate.group);
    const operator = predicate.op === "not-in" ? "NOT IN" : "IN";
    const rate = predicate.rate ? ` ${formatLayerFilterRateComparison(predicate.rate)}` : "";
    return subject && group ? `${subject} ${operator} ${group}${rate}` : "";
  }
  if (predicate.kind === "group-rate") {
    const comparison = formatLayerFilterRateComparison(predicate.comparison);
    return predicate.group && comparison ? `group:${safeDslValue(predicate.group)} ${comparison}` : "";
  }
  if (predicate.kind === "property-compare") {
    const operator = LAYER_FILTER_COMPARE_OPERATOR_TEXT[normalizeLayerFilterCompareOperator(predicate.op)] || "";
    const value = formatLayerFilterTypedValue(predicate.value);
    return predicate.field && operator && value ? `${safeDslValue(predicate.field)} ${operator} ${value}` : "";
  }
  if (predicate.kind === "relation") {
    const target = formatLayerFilterSubject(predicate.target);
    return predicate.relation && target ? `${safeDslValue(predicate.relation)} ${target}` : "";
  }
  return "";
}

function serializeTerm(term) {
  const normalized = normalizeSearchTerm(term);
  if (!normalized) {
    return "";
  }
  if (normalized.kind === "title") return `title:${normalized.titleId}`;
  if (normalized.kind === "fishing-contest") {
    return "contest";
  }
  if (normalized.kind === "status") {
    return `status:${safeDslValue(normalized.status)}`;
  }
  if (normalized.kind === "grade") {
    return `grade:${safeDslValue(normalized.grade)}`;
  }
  if (normalized.kind === "fish") {
    return `fish:${normalized.fishId}`;
  }
  if (normalized.kind === "item") {
    return `item:${normalized.itemId}${formatSourceScope(normalized.sourceKinds)}`;
  }
  if (normalized.kind === "item-group") {
    return `items:[${normalized.itemIds.join(",")}]${formatSourceScope(normalized.sourceKinds)}`;
  }
  if (normalized.kind === "zone") {
    return `zone:${formatZoneRgb(normalized.zoneRgb)}`;
  }
  if (normalized.kind === "semantic") {
    if (normalized.layerId === "regions") {
      return `region:${normalized.fieldId}`;
    }
    if (normalized.layerId === "region_groups") {
      return `regiongroup:${normalized.fieldId}`;
    }
    return `semantic:${safeDslValue(normalized.layerId)}:${normalized.fieldId}`;
  }
  const tradeNpcKey = tradeNpcKeyFromSearchTerm(normalized);
  if (tradeNpcKey != null) {
    return `npc:${tradeNpcKey}`;
  }
  if (normalized.kind === "layer-fact") {
    return `fact:${safeDslValue(normalized.layerId)}:${safeDslValue(normalized.factId)}${formatLayerFactConditions(normalized.conditions)}`;
  }
  if (normalized.kind === "layer-filter") {
    const predicate = formatLayerFilterPredicate(normalized.predicate);
    return predicate ? `filter:${safeDslValue(normalized.layerId)}[${predicate}]` : "";
  }
  if (normalized.kind === "patch-bound") {
    const bound = normalizePatchBound(normalized.bound);
    const patchId = normalizePatchId(normalized.patchId);
    return bound && patchId ? `${bound}:${safeDslValue(patchId)}` : "";
  }
  return "";
}

function serializeNode(node, parentPrecedence = 0) {
  if (!isPlainObject(node)) {
    return "";
  }
  const patchWindow = patchWindowFromExpressionNode(node);
  if (patchWindow) {
    const patchText = serializePatchBounds(patchWindow);
    const text = patchText.join(" AND ");
    return patchText.length > 1 && PRECEDENCE.and < parentPrecedence ? `(${text})` : text;
  }
  if (node.type === "term") {
    const termText = serializeTerm(node.term);
    if (!termText) {
      return "";
    }
    return normalizeSearchExpressionNegated(node.negated) ? `NOT ${termText}` : termText;
  }
  if (node.type === "container") {
    const expressionText = serializeNode(node.expression);
    if (!expressionText) {
      return "";
    }
    const text = `[${expressionText}]`;
    return normalizeSearchExpressionNegated(node.negated) ? `NOT ${text}` : text;
  }
  if (node.type !== "group") {
    return "";
  }
  const operator = normalizeSearchExpressionOperator(node.operator);
  const precedence = PRECEDENCE[operator];
  const nodeNegated = normalizeSearchExpressionNegated(node.negated);
  const localPatchWindow = { fromPatchId: "", toPatchId: "" };
  const childText = [];
  for (const child of Array.isArray(node.children) ? node.children : []) {
    if (operator === "and" && !nodeNegated) {
      const childPatchWindow = patchWindowFromExpressionNode(child);
      if (childPatchWindow) {
        mergePatchWindow(localPatchWindow, childPatchWindow);
        continue;
      }
    }
    const text = serializeNode(child, precedence);
    if (
      text &&
      operator === "or" &&
      child?.type === "group" &&
      normalizeSearchExpressionOperator(child.operator) === "and" &&
      searchExpressionNodeHasPatchBound(child)
    ) {
      childText.push(`(${text})`);
      continue;
    }
    if (text) {
      childText.push(text);
    }
  }
  if (operator === "and" && !nodeNegated) {
    childText.push(...serializePatchBounds(localPatchWindow));
  }
  if (!childText.length) {
    return "";
  }
  let text = childText.join(` ${operator.toUpperCase()} `);
  if (nodeNegated) {
    text = `NOT (${text})`;
  } else if (precedence < parentPrecedence && childText.length > 1) {
    text = `(${text})`;
  }
  return text;
}

function serializePatchBounds(bounds) {
  const source = isPlainObject(bounds) ? bounds : {};
  const fromPatchId = normalizePatchId(source.fromPatchId ?? source.from);
  const toPatchId = normalizePatchId(source.toPatchId ?? source.to);
  if (fromPatchId && toPatchId && fromPatchId === toPatchId) {
    return [`patch:${safeDslValue(fromPatchId)}`];
  }
  return [
    fromPatchId ? `from:${safeDslValue(fromPatchId)}` : "",
    toPatchId ? `to:${safeDslValue(toPatchId)}` : "",
  ].filter(Boolean);
}

function mergePatchWindow(target, source) {
  const fromPatchId = normalizePatchId(source?.fromPatchId);
  const toPatchId = normalizePatchId(source?.toPatchId);
  if (fromPatchId) {
    target.fromPatchId = fromPatchId;
  }
  if (toPatchId) {
    target.toPatchId = toPatchId;
  }
}

export function serializeSearchExpressionDsl(expression) {
  const normalized = normalizeSearchExpression(expression);
  if (!normalized) {
    return "";
  }
  const envelope = normalizeSearchQueryEnvelope(normalized);
  const patchText = serializePatchBounds(envelope.globalEvidenceWindow);
  const expressionText = envelope.filter
    ? serializeNode(envelope.filter, patchText.length ? PRECEDENCE.and : 0)
    : "";
  return [...patchText, expressionText].filter(Boolean).join(" AND ");
}
